import './Table.css';

import { type ComponentProps, type ReactNode, useMemo, useState } from 'react';

import { IconButton } from '../IconButton/IconButton.component.tsx';
import { Sort } from '../Icons';

interface TableProps<
  Element,
  Column extends string,
> extends ComponentProps<'table'> {
  columns: Column[];
  getColumnName: (column: Column) => string;
  elements: Element[];
  getRow: (row: Element) => ReactNode[];
  getRowId: (row: Element) => string;
  sortFn: (column: Column) => (e1: Element, e2: Element) => number;
  onRowClick: (row: Element) => void;
}

export const Table = <Element, Column extends string>({
  columns,
  getColumnName,
  elements,
  getRow,
  getRowId,
  sortFn,
  onRowClick,
  ...props
}: TableProps<Element, Column>): ReactNode => {
  const [columnForSort, setColumnForSort] = useState<Column | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const onColumnSortClick = (column: Column): void => {
    if (columnForSort === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setColumnForSort(column);
      setSortDirection('asc');
    }
  };

  const headers = columns.map((column, index) => (
    <th key={column} className="table-cell">
      <div
        className={`column-header-container ${index === columns.length - 1 ? 'column-header-container-last' : ''}`}
      >
        <p className="text-sm regular">{getColumnName(column)}</p>
        {index < columns.length - 1 && (
          <IconButton
            data-testid={`column-${column}-sort`}
            Icon={Sort}
            size={16}
            onClick={() => {
              onColumnSortClick(column);
            }}
          />
        )}
      </div>
    </th>
  ));

  const compareFn = useMemo(() => {
    if (columnForSort === null) {
      return undefined;
    } else {
      const sortFunction = sortFn(columnForSort);
      return sortDirection === 'asc'
        ? sortFunction
        : (e1: Element, e2: Element) => -sortFunction(e1, e2);
    }
  }, [columnForSort, sortDirection, sortFn]);

  const rows = elements.sort(compareFn).map((element: Element) => {
    const rowId = getRowId(element);

    return (
      <tr
        key={rowId}
        onClick={() => {
          onRowClick(element);
        }}
        className="table-row"
      >
        {getRow(element).map((cell, index) => (
          <td key={`${rowId}-${index.toString()}`} className="table-cell">
            {cell}
          </td>
        ))}
      </tr>
    );
  });

  return (
    <table className="table" {...props}>
      <thead className="table-header">
        <tr className="table-header-row">{headers}</tr>
      </thead>
      <tbody className="table-body">{rows}</tbody>
    </table>
  );
};
